import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { label: "Engineering Architecture", to: "/engineering-architecture" },
  { label: "Sectors", to: "/sectors" },
  { label: "Cierge Design Studio", to: "/cierge-studio" },
  { label: "Evidence", to: "/evidence" },
  { label: "Company", to: "/company" },
];

function Wordmark() {
  return (
    <Link to="/" className="group flex items-baseline gap-2" data-testid="nav-logo">
      <span className="font-serif text-xl tracking-tight text-ink">KATLAS</span>
      <span className="font-mono text-[10px] tracking-[0.32em] text-ink/50 uppercase">
        Technology
      </span>
    </Link>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`no-print fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-stone/90 backdrop-blur-[2px] border-b border-ink/12" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Wordmark />

        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative font-sans text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-teal" : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-teal transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/company#contact"
            data-testid="nav-bring-journey-cta"
            className="group hidden items-center gap-2 bg-teal px-5 py-2.5 font-sans text-sm font-medium text-stone transition-colors duration-300 hover:bg-ink sm:inline-flex"
          >
            Bring us a journey
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
          </Link>
          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center border border-ink/20 text-ink lg:hidden"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-ink/12 bg-stone lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className="border-b border-ink/10 py-4 font-serif text-2xl text-ink"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/company#contact"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-teal px-5 py-3.5 font-sans text-sm font-medium text-stone"
                data-testid="nav-mobile-cta"
              >
                Bring us a journey
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
